import React from "react";
import { Lock, Globe2, ShieldCheck, BadgeCheck } from "lucide-react";

const Chip = ({ label, dark, children }) => (
  <span aria-label={label} title={label}
    className={`inline-flex items-center justify-center h-9 px-3 rounded-lg border shadow-sm transition-transform duration-200 ease-out hover:-translate-y-0.5 hover:shadow-md ${dark ? "bg-white border-white/15" : "bg-white border-[#E5E7EB]"}`}>
    {children}
  </span>
);

const VisaMark = () => (
  <svg viewBox="0 0 48 16" className="h-4 w-auto" role="img" aria-label="Visa">
    <text x="0" y="13" fontFamily="Helvetica, Arial, sans-serif" fontSize="15" fontWeight="800" fontStyle="italic" fill="#1434CB" letterSpacing="0.5">VISA</text>
  </svg>
);

const MastercardMark = () => (
  <svg viewBox="0 0 36 22" className="h-[22px] w-auto" role="img" aria-label="Mastercard">
    <circle cx="13" cy="11" r="10" fill="#EB001B" />
    <circle cx="23" cy="11" r="10" fill="#F79E1B" />
    <path d="M18 3.2a10 10 0 010 15.6 10 10 0 010-15.6z" fill="#FF5F00" />
  </svg>
);

const GooglePayMark = () => (
  <svg viewBox="0 0 74 24" className="h-[18px] w-auto" role="img" aria-label="Google Pay">
    <g transform="translate(0,1) scale(0.46)">
      <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 5.1 29.3 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.4-.1-2.7-.4-3.9z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 18.9 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 5.1 29.3 3 24 3 15.9 3 8.9 7.6 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 45c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 36.1 26.7 37 24 37c-5.2 0-9.6-3.3-11.3-8l-6.5 5C8.8 40.3 15.8 45 24 45z"/>
      <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 45 33 45 24c0-1.4-.1-2.7-.4-3.9z"/>
    </g>
    <text x="26" y="17" fontFamily="Helvetica, Arial, sans-serif" fontSize="15" fontWeight="600" fill="#5F6368">Pay</text>
  </svg>
);

const ApplePayMark = () => (
  <svg viewBox="0 0 58 24" className="h-[18px] w-auto" role="img" aria-label="Apple Pay">
    <g transform="translate(0,3.2) scale(0.034)">
      <path fill="#000" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
    </g>
    <text x="18" y="17" fontFamily="Helvetica, Arial, sans-serif" fontSize="15" fontWeight="600" fill="#000">Pay</text>
  </svg>
);

export const PaymentMethodIcons = ({ dark = false, className = "" }) => (
  <div className={`flex flex-wrap items-center justify-center gap-3 ${className}`} data-testid="payment-method-icons">
    <Chip label="Visa" dark={dark}><VisaMark /></Chip>
    <Chip label="Mastercard" dark={dark}><MastercardMark /></Chip>
    <Chip label="Google Pay" dark={dark}><GooglePayMark /></Chip>
    <Chip label="Apple Pay" dark={dark}><ApplePayMark /></Chip>
  </div>
);

const BADGES = [
  { icon: Lock, t: "SSL Secured" },
  { icon: ShieldCheck, t: "PCI-DSS Compliant" },
  { icon: Globe2, t: "International Travel Company" },
  { icon: BadgeCheck, t: "Protected Checkout" },
];

export const TrustBadges = ({ dark = false, className = "" }) => (
  <div className={`flex flex-wrap items-center gap-x-6 gap-y-2.5 ${className}`} data-testid="trust-badges">
    {BADGES.map(({ icon: Icon, t }) => (
      <span key={t} className={`inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wide ${dark ? "text-slate-400" : "text-[#4B5563]"}`}>
        <Icon size={14} className="text-[#D4A62A]" strokeWidth={2} /> {t}
      </span>
    ))}
  </div>
);

export const SecurePaymentsPanel = () => (
  <div className="bg-white border border-[#EDEFF2] rounded-[18px] shadow-[0_18px_45px_rgba(10,46,77,0.08)] px-8 sm:px-14 py-10 flex flex-col items-center gap-5 max-w-3xl mx-auto"
    data-testid="secure-payments-panel">
    <div className="flex items-center gap-2.5">
      <span className="w-8 h-8 rounded-full bg-[#FDF8EC] flex items-center justify-center"><Lock size={14} className="text-[#D4A62A]" strokeWidth={2.2} /></span>
      <h3 className="text-[13px] uppercase tracking-[0.22em] font-bold text-[#0A2E4D]">Secure Online Payments</h3>
    </div>
    <PaymentMethodIcons />
    <p className="text-xs text-[#9CA3AF] tracking-wide">Pay safely using internationally accepted payment methods.</p>
    <div className="w-16 border-t border-[#EDEFF2]" />
    <TrustBadges className="justify-center" />
  </div>
);
